import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  PettyCashVoucherDto,
  PettyCashVoucherListDto,
  PettyCashVoucherFilterDto,
  CreatePettyCashVoucherRequest,
  UpdatePettyCashVoucherRequest,
  PettyCashRegisterRowDto,
  PettyCashRegisterFilterDto,
  PettyCashFloatSummaryRowDto,
} from '@/types/pettyCashVoucher.types';

export const pettyCashVoucherApi = {
  /** GET /api/v1/petty-cash/vouchers */
  getList: (filter?: PettyCashVoucherFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<PettyCashVoucherListDto>>>('/petty-cash/vouchers', {
      params: filter,
    }),

  /** GET /api/v1/petty-cash/vouchers/{id} */
  getById: (id: string) =>
    apiClient.get<ApiResponse<PettyCashVoucherDto>>(`/petty-cash/vouchers/${id}`),

  /** POST /api/v1/petty-cash/vouchers */
  create: (data: CreatePettyCashVoucherRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/petty-cash/vouchers', data),

  /** PUT /api/v1/petty-cash/vouchers/{id} — Draft only */
  update: (id: string, data: UpdatePettyCashVoucherRequest) =>
    apiClient.put<ApiResponse<object>>(`/petty-cash/vouchers/${id}`, data),

  /** POST /api/v1/petty-cash/vouchers/{id}/submit */
  submit: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/petty-cash/vouchers/${id}/submit`),

  /** POST /api/v1/petty-cash/vouchers/{id}/approve */
  approve: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/petty-cash/vouchers/${id}/approve`),

  /** POST /api/v1/petty-cash/vouchers/{id}/reject */
  reject: (id: string, reason: string) =>
    apiClient.post<ApiResponse<object>>(`/petty-cash/vouchers/${id}/reject`, { reason }),

  /** POST /api/v1/petty-cash/vouchers/{id}/post — the only step that moves cash */
  post: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/petty-cash/vouchers/${id}/post`),

  /** POST /api/v1/petty-cash/vouchers/{id}/cancel — before it is posted */
  cancel: (id: string, reason?: string | null) =>
    apiClient.post<ApiResponse<object>>(`/petty-cash/vouchers/${id}/cancel`, { reason }),

  /** POST /api/v1/petty-cash/vouchers/{id}/reverse — undoes a posted voucher */
  reverse: (id: string, reversalDate: string, reversalPostingDate: string, reason?: string | null) =>
    apiClient.post<ApiResponse<{ id: string }>>(`/petty-cash/vouchers/${id}/reverse`, {
      reversalDate, reversalPostingDate, reason,
    }),

  /** GET /api/v1/petty-cash/vouchers/register — the Petty Cash Register report */
  getRegister: (filter?: PettyCashRegisterFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<PettyCashRegisterRowDto>>>('/petty-cash/vouchers/register', {
      params: filter,
    }),

  /** GET /api/v1/petty-cash/vouchers/summary — the Float Balance Summary report */
  getSummary: () =>
    apiClient.get<ApiResponse<PettyCashFloatSummaryRowDto[]>>('/petty-cash/vouchers/summary'),
};
